import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.#hydrateVoltMegaFeatureSources();
    requestAnimationFrame(() => this.#hydrateVoltMegaFeatureSources());
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#hydrateVoltMegaFeatureSources();
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  #hydrateVoltMegaFeatureSources() {
    const sourceItems = Array.from(
      document.querySelectorAll('header-menu .salcombe-mega-feature-source[data-volt-feature-target]')
    ).filter((item) => item instanceof HTMLElement && item.dataset.voltFeatureTarget);

    if (!sourceItems.length) return;

    /** @type {Map<string, HTMLElement[]>} */
    const sourcesByTarget = new Map();

    sourceItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const target = item.dataset.voltFeatureTarget;
      if (!target) return;

      const sources = sourcesByTarget.get(target) || [];
      sources.push(item);
      sourcesByTarget.set(target, sources);
    });

    this.querySelectorAll('.salcombe-mega-drawer__panel-content[data-volt-menu-handle]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const target = panel.dataset.voltMenuHandle || panel.dataset.voltFeatureTargetHint;
      if (!target) return;

      const sources = sourcesByTarget.get(target) || [];
      if (!sources.length) return;

      panel.querySelectorAll('[data-volt-client-features="true"]').forEach((element) => element.remove());

      const serverFeatures = panel.querySelector('.salcombe-mega-drawer__features:not([data-volt-client-features])');
      if (serverFeatures) return;

      const features = document.createElement('div');
      features.className = 'salcombe-mega-drawer__features';
      features.dataset.voltClientFeatures = 'true';

      const list = document.createElement('ul');
      list.className = 'salcombe-mega-drawer__feature-list list-unstyled';
      list.setAttribute('role', 'list');

      let renderedCount = 0;

      sources.forEach((source) => {
        const card = source.querySelector('.salcombe-mega-feature');
        if (!card) return;

        const item = document.createElement('li');
        item.className = 'salcombe-mega-drawer__feature-item';
        item.dataset.voltFeatureTarget = source.dataset.voltFeatureTarget || '';
        item.append(card.cloneNode(true));
        list.append(item);
        renderedCount += 1;
      });

      if (!renderedCount) return;

      features.append(list);
      panel.append(features);
      panel.dataset.voltFeatureCount = String(renderedCount);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
